import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { endImpersonation, forceEndImpersonation } from '../../store/actions/impersonation.action';
import AlertDialogModal from './AlertDialog';
import './ImpersonationBanner.css';

const READONLY_BODY_CLASS = 'impersonation-readonly';
const WARN_5_MIN_MS = 5 * 60 * 1000;
const WARN_1_MIN_MS = 60 * 1000;

const formatRemaining = (ms) => {
    if (ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const ImpersonationBanner = () => {
    const dispatch = useDispatch();
    const impersonation = useSelector((state) => state?.impersonation);
    const [exiting, setExiting] = useState(false);
    const [remainingMs, setRemainingMs] = useState(null);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    const warned5Ref = useRef(false);
    const warned1Ref = useRef(false);
    const expiredHandledRef = useRef(false);

    useEffect(() => {
        if (impersonation?.active && impersonation?.isReadOnly !== false) {
            document.body.classList.add(READONLY_BODY_CLASS);
        } else {
            document.body.classList.remove(READONLY_BODY_CLASS);
        }
        return () => document.body.classList.remove(READONLY_BODY_CLASS);
    }, [impersonation?.active, impersonation?.isReadOnly]);

    const expiresAt = impersonation?.expiresAt;
    const active = !!impersonation?.active;

    useEffect(() => {
        if (!active || !expiresAt) {
            setRemainingMs(null);
            warned5Ref.current = false;
            warned1Ref.current = false;
            expiredHandledRef.current = false;
            return undefined;
        }

        const expiryTs = new Date(expiresAt).getTime();
        if (Number.isNaN(expiryTs)) return undefined;

        const tick = () => {
            const remaining = expiryTs - Date.now();
            setRemainingMs(remaining);

            if (remaining <= 0) {
                if (!expiredHandledRef.current) {
                    expiredHandledRef.current = true;
                    setShowExpiredModal(true);
                    dispatch(forceEndImpersonation());
                }
                return;
            }
            if (remaining <= WARN_1_MIN_MS && !warned1Ref.current) {
                warned1Ref.current = true;
                toast.warning('Impersonation session ends in 1 minute.');
            } else if (remaining <= WARN_5_MIN_MS && !warned5Ref.current) {
                warned5Ref.current = true;
                toast.warning('Impersonation session ends in 5 minutes. Save any in-progress work.');
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [active, expiresAt, dispatch]);

    if (showExpiredModal) {
        return (
            <AlertDialogModal
                modalShow
                headingText="Impersonation Session Ended"
                messageText="Your impersonation session has ended. You have been returned to your Super Admin account."
                default
                zIndex={1600}
                handleCancelDeleteAlert={() => {
                    setShowExpiredModal(false);
                    globalThis.location.href = '/companyManagement';
                }}
            />
        );
    }

    if (!active) return null;

    const target = impersonation?.targetUserName || 'user';
    const origin = impersonation?.impersonatedByUserName;
    const isReadOnly = impersonation?.isReadOnly !== false;
    const showCountdown = remainingMs !== null && remainingMs > 0;

    const handleExit = () => {
        setExiting(true);
        Promise.resolve(dispatch(endImpersonation()))
            .then(() => {
                globalThis.location.href = '/companyManagement';
            })
            .catch(() => {
                toast.error('Failed to exit impersonation');
                setExiting(false);
            });
    };

    return (
        <div className="impersonationBanner" role="alert">
            <span className="impersonationBanner-message">
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                <span>
                    You are impersonating <strong>{target}</strong>
                    {origin ? <> (logged in as <strong>{origin}</strong>)</> : null}
                    {isReadOnly && <>{' '}&mdash; Read-Only Mode</>}
                </span>
            </span>
            <div className="impersonationBanner-actions">
                <button
                    type="button"
                    className="impersonationBanner-exitBtn"
                    onClick={handleExit}
                    disabled={exiting}
                >
                    {exiting ? 'Exiting…' : 'Exit Impersonation'}
                </button>
                {showCountdown && (
                    <span className="impersonationBanner-countdown" aria-live="polite">
                        Ends in {formatRemaining(remainingMs)}
                    </span>
                )}
            </div>
        </div>
    );
};

export default ImpersonationBanner;
